import type { HouseholdState, HouseholdMember, Store, InventoryItem, Meal, StoreTrip, ShoppingItem, DiscoveredStore, HouseholdLocation, StoreDiscoveryResult, PriceCatcherResult } from '../types';
import { supabase } from './supabase';

export const MEDIA_BUCKET = 'weekstead-household-media';

function ensureClient() {
  if (!supabase) throw new Error('Weekstead backend is not configured.');
  return supabase;
}

async function edgeErrorMessage(error: any) {
  const context = error?.context;
  if (context && typeof context.clone === 'function') {
    try {
      const payload = await context.clone().json();
      if (payload?.error) return String(payload.error);
    } catch {
      try {
        const text = await context.clone().text();
        if (text) return text.slice(0, 240);
      } catch { /* use the library message */ }
    }
  }
  return error?.message ? String(error.message) : 'Weekstead could not complete that request.';
}

async function invokeEdge<T>(name: string, body: Record<string, any>): Promise<T> {
  const client = ensureClient();
  const { data, error } = await client.functions.invoke(name, { body });
  if (error) throw new Error(await edgeErrorMessage(error));
  if (data?.error) throw new Error(String(data.error));
  return data as T;
}

function dateInTimeZone(date: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

function addCalendarDays(value: string, days: number) {
  const d = new Date(`${value}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function currentWeekBounds(timeZone: string) {
  const today = dateInTimeZone(new Date(), timeZone);
  const d = new Date(`${today}T12:00:00Z`);
  const day = d.getUTCDay();
  const weekStart = addCalendarDays(today, day === 0 ? -6 : 1 - day);
  return { today, weekStart, weekEnd: addCalendarDays(weekStart, 6) };
}

function dayName(value: string, timeZone?: string) {
  const date = value.includes('T') ? new Date(value) : new Date(`${value}T12:00:00Z`);
  return date.toLocaleDateString('en-CA', { weekday: 'long', ...(timeZone ? { timeZone } : {}) });
}

function daysUntil(value: string | null) {
  if (!value) return 30;
  return Math.max(0, Math.ceil((new Date(value).getTime() - Date.now()) / 86400000));
}

function mapStoreType(value: string): Store['type'] {
  if (value === 'warehouse') return 'warehouse';
  if (value === 'asian_market') return 'asian-market';
  if (value === 'specialty' || value === 'farmers_market') return 'specialty';
  return 'supermarket';
}

function normalizedItemName(value: unknown) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
}

function priceSourceRank(source: string) {
  if (source === 'live_api') return 0;
  if (source === 'store_feed') return 1;
  if (source === 'receipt') return 2;
  if (source === 'manual') return 3;
  return 4;
}

export async function getCurrentHouseholdId() {
  const client = ensureClient();
  const { data: auth } = await client.auth.getUser();
  if (!auth.user) return null;
  const { data, error } = await client.from('household_memberships').select('household_id').eq('user_id', auth.user.id).limit(1).maybeSingle();
  if (error) throw error;
  return data?.household_id ?? null;
}

export async function loadHouseholdState(): Promise<{ householdId: string | null; state: HouseholdState | null }> {
  const client = ensureClient();
  const householdId = await getCurrentHouseholdId();
  if (!householdId) return { householdId: null, state: null };

  const householdRes = await client.from('households').select('*').eq('id', householdId).single();
  if (householdRes.error) throw householdRes.error;
  const timeZone = householdRes.data.timezone || 'America/Vancouver';
  const { weekStart, weekEnd } = currentWeekBounds(timeZone);
  const planRes = await client.from('weekly_plans').select('id').eq('household_id', householdId).eq('week_start', weekStart).eq('status', 'active').limit(1).maybeSingle();
  if (planRes.error) throw planRes.error;

  const [peopleRes, storesRes, inventoryRes, mealsRes, tripsRes, productsRes] = await Promise.all([
    client.from('people').select('*').eq('household_id', householdId).eq('active', true).order('created_at'),
    client.from('stores').select('*').eq('household_id', householdId).eq('active', true).order('priority'),
    client.from('inventory_items').select('*').eq('household_id', householdId).gt('quantity', 0).order('expires_at', { ascending: true, nullsFirst: false }),
    client.from('meals').select('*').eq('household_id', householdId).gte('service_date', weekStart).lte('service_date', weekEnd).order('service_date'),
    planRes.data?.id
      ? client.from('shopping_trips').select('*').eq('household_id', householdId).eq('weekly_plan_id', planRes.data.id).in('status', ['planned', 'active']).order('planned_for')
      : Promise.resolve({ data: [], error: null } as any),
    client.from('store_products').select('*').eq('household_id', householdId).eq('active', true).eq('is_sponsored', false).order('observed_at', { ascending: false }),
  ]);

  const firstError = [peopleRes, storesRes, inventoryRes, mealsRes, tripsRes, productsRes].find((x) => x.error)?.error;
  if (firstError) throw firstError;

  const mealsData = mealsRes.data ?? [];
  const tripsData = tripsRes.data ?? [];
  const priceRows = (productsRes.data ?? []).filter((p: any) => p.price != null && (!p.price_expires_at || new Date(p.price_expires_at).getTime() > Date.now()));
  const [ingredientRes, itemRes] = await Promise.all([
    mealsData.length ? client.from('meal_ingredients').select('*').eq('household_id', householdId).in('meal_id', mealsData.map((m) => m.id)) : Promise.resolve({ data: [], error: null } as any),
    tripsData.length ? client.from('shopping_items').select('*').eq('household_id', householdId).in('trip_id', tripsData.map((t: any) => t.id)).order('created_at') : Promise.resolve({ data: [], error: null } as any),
  ]);
  if (ingredientRes.error) throw ingredientRes.error;
  if (itemRes.error) throw itemRes.error;

  const members: HouseholdMember[] = (peopleRes.data ?? []).map((p) => ({
    id: p.id,
    name: p.display_name,
    role: p.person_type === 'child' ? 'child' : 'adult',
    portionFactor: Number(p.portion_factor),
    dislikes: p.dislikes ?? [],
    allergies: p.allergies ?? [],
    dietaryRules: p.dietary_rules ?? [],
  }));

  const stores: Store[] = (storesRes.data ?? []).map((s) => ({
    id: s.id,
    name: s.name,
    type: mapStoreType(s.store_type),
    cadenceDays: s.cadence_days,
    priority: s.priority,
    formattedAddress: s.formatted_address ?? s.address ?? null,
    distanceKm: s.distance_km == null ? null : Number(s.distance_km),
    discovered: Boolean(s.discovered),
    priceProvider: s.price_provider ?? null,
    priceProviderBanner: s.price_provider_banner ?? null,
    priceProviderStoreId: s.price_provider_store_id ?? null,
    priceLastCheckedAt: s.price_last_checked_at ?? null,
    priceLastError: s.price_last_error ?? null,
  }));

  const inventory: InventoryItem[] = (inventoryRes.data ?? []).map((i) => ({
    id: i.id,
    name: i.name,
    quantity: Number(i.quantity),
    unit: i.unit,
    zone: ['fridge', 'freezer', 'pantry'].includes(i.storage_zone) ? i.storage_zone : 'pantry',
    expiresInDays: daysUntil(i.expires_at),
    typicalWeeklyUse: Number(i.quantity),
    category: i.category ?? 'Other',
  })) as InventoryItem[];

  const ingredientsByMeal = new Map<string, any[]>();
  for (const row of ingredientRes.data ?? []) {
    const list = ingredientsByMeal.get(row.meal_id) ?? [];
    list.push({ name: row.ingredient_name, quantity: Number(row.quantity), unit: row.unit });
    ingredientsByMeal.set(row.meal_id, list);
  }

  const meals: Meal[] = mealsData.map((m) => ({
    id: m.id,
    day: dayName(m.service_date, timeZone),
    serviceDate: m.service_date,
    mealType: m.meal_type,
    name: m.name,
    minutes: m.prep_minutes ?? 0,
    servings: Number(m.servings),
    ingredients: ingredientsByMeal.get(m.id) ?? [],
    reason: m.plan_reason ?? 'Planned around your household preferences and kitchen inventory.',
    feedback: m.feedback ?? undefined,
    status: m.status ?? 'planned',
    cookingSteps: Array.isArray(m.cooking_steps) ? m.cooking_steps : [],
    cookingStartedAt: m.cooking_started_at ?? null,
    cookedAt: m.cooked_at ?? null,
  }));

  const shoppingByTrip = new Map<string, ShoppingItem[]>();
  for (const row of itemRes.data ?? []) {
    const storeId = tripsData.find((t: any) => t.id === row.trip_id)?.store_id ?? '';
    const matches = priceRows
      .filter((p: any) => p.store_id === storeId && normalizedItemName(p.product_name) === normalizedItemName(row.name))
      .sort((a: any, b: any) => priceSourceRank(a.source) - priceSourceRank(b.source) || new Date(b.observed_at).getTime() - new Date(a.observed_at).getTime());
    const price = matches[0];
    const list = shoppingByTrip.get(row.trip_id) ?? [];
    list.push({
      id: row.id,
      name: row.name,
      quantity: Number(row.package_count && row.package_quantity ? Number(row.package_count) * Number(row.package_quantity) : row.quantity),
      unit: row.unit,
      section: row.section ?? 'Shopping list',
      storeId,
      reason: row.plan_reason ?? 'Needed for this week.',
      checked: row.checked,
      priceInfo: price ? {
        price: Number(price.price),
        regularPrice: price.regular_price == null ? null : Number(price.regular_price),
        retailerProductName: price.retailer_product_name ?? null,
        packageQuantity: price.package_quantity == null ? null : Number(price.package_quantity),
        packageUnit: price.package_unit ?? null,
        unitPrice: price.unit_price == null ? null : Number(price.unit_price),
        unitPriceUnit: price.unit_price_unit ?? null,
        source: price.source,
        sourceProvider: price.source_provider ?? null,
        observedAt: price.observed_at ?? null,
        expiresAt: price.price_expires_at ?? null,
        onSale: Boolean(price.on_sale),
        isSponsored: Boolean(price.is_sponsored),
        sponsorName: price.sponsor_name ?? null,
      } : null,
    });
    shoppingByTrip.set(row.trip_id, list);
  }

  const trips: StoreTrip[] = tripsData.map((t: any) => ({
    id: t.id,
    storeId: t.store_id,
    day: dayName(t.planned_for, timeZone),
    plannedFor: t.planned_for,
    estimatedCost: Number(t.estimated_cost ?? 0),
    items: shoppingByTrip.get(t.id) ?? [],
  }));

  return {
    householdId,
    state: {
      householdName: householdRes.data.name,
      members,
      stores,
      inventory,
      meals,
      trips,
      weeklyBudget: Number(householdRes.data.weekly_budget ?? 220),
      shopDaysPerWeek: householdRes.data.shop_days_per_week,
      kidFriendlyMode: Boolean(householdRes.data.kid_friendly_mode),
      timezone: timeZone,
      locationLabel: householdRes.data.location_label ?? null,
      groceryRadiusKm: householdRes.data.grocery_radius_km == null ? null : Number(householdRes.data.grocery_radius_km),
    },
  };
}

export async function discoverNearbyStores(input: { latitude?: number; longitude?: number; address?: string; regionCode?: string; maxDistanceKm?: number }): Promise<StoreDiscoveryResult> {
  try {
    return await invokeEdge<StoreDiscoveryResult>('discover-stores', input);
  } catch (error) {
    if (error instanceof Error && error.message.includes('GOOGLE_MAPS_NOT_CONFIGURED')) throw new Error('Nearby-store discovery still needs its Google Maps key. You can continue with manual stores for now.');
    throw error;
  }
}

export async function createHousehold(input: {
  name: string;
  ownerName: string;
  weeklyBudget: number;
  shopDaysPerWeek: number;
  adults: number;
  children: number;
  stores: (string | DiscoveredStore)[];
  location?: HouseholdLocation | null;
}) {
  const data = await invokeEdge<{ householdId: string }>('create-household', input);
  return data.householdId;
}

export async function rebuildWeek(householdId: string, instruction = 'Build the best household food plan for this week') {
  return invokeEdge<{ ok: boolean; mode: string; model?: string | null; summary: string; aiConfigured?: boolean }>('plan-week', { householdId, instruction });
}

export async function refreshShoppingRemote(householdId: string) {
  return invokeEdge<{ ok: boolean; summary: string; shoppingItems: number; stores: number }>('refresh-shopping', { householdId });
}

export async function refreshPricesRemote(householdId: string, scope: 'planned'|'compare' = 'compare') {
  return invokeEdge<PriceCatcherResult>('price-catcher', { householdId, scope, maxNetwork: 18 });
}

export async function swapMealRemote(mealId: string) {
  return invokeEdge<{ ok: boolean; mode: string; model?: string | null; mealId: string; oldMealName: string; newMealName: string; summary: string }>('swap-meal', { mealId });
}

export async function startCookingRemote(mealId: string) {
  return invokeEdge<{ ok: boolean; mealId: string; steps: string[]; mode: string; status: string }>('meal-guide', { mealId, action: 'start' });
}

export async function finishCookingRemote(mealId: string) {
  return invokeEdge<{ ok: boolean; mealId: string; status: string; cookedAt?: string; alreadyCompleted?: boolean }>('meal-guide', { mealId, action: 'finish' });
}

export async function setMealFeedbackRemote(householdId: string, mealId: string, feedback: 'loved' | 'okay' | 'skip') {
  const client = ensureClient();
  const { error } = await client.from('meals').update({ feedback }).eq('household_id', householdId).eq('id', mealId);
  if (error) throw error;
}

export async function updateInventoryQuantityRemote(householdId: string, id: string, quantity: number) {
  const client = ensureClient();
  const { error } = await client.from('inventory_items').update({ quantity }).eq('household_id', householdId).eq('id', id);
  if (error) throw error;
}

export async function toggleShoppingItemRemote(householdId: string, id: string, checked: boolean) {
  const client = ensureClient();
  const { error } = await client.from('shopping_items').update({ checked }).eq('household_id', householdId).eq('id', id);
  if (error) throw error;
}

export async function addHouseholdMemberRemote(householdId: string, input: { name: string; role: 'adult' | 'child' }) {
  const client = ensureClient();
  const { error } = await client.from('people').insert({ household_id: householdId, display_name: input.name, person_type: input.role, portion_factor: input.role === 'child' ? 0.7 : 1 });
  if (error) throw error;
}

export async function uploadKitchenPhoto(householdId: string, zone: 'fridge' | 'freezer' | 'pantry', file: File) {
  const client = ensureClient();
  const { data: auth } = await client.auth.getUser();
  if (!auth.user) throw new Error('Please sign in again.');
  const safe = file.name.toLowerCase().replace(/[^a-z0-9._-]+/g, '-').slice(-80) || 'kitchen.jpg';
  const path = `${householdId}/kitchen/${crypto.randomUUID()}-${safe}`;
  const upload = await client.storage.from(MEDIA_BUCKET).upload(path, file, { contentType: file.type || 'image/jpeg', upsert: false });
  if (upload.error) throw upload.error;
  const scan = await client.from('kitchen_scans').insert({ household_id: householdId, storage_zone: zone, object_path: path, created_by: auth.user.id }).select('id').single();
  if (scan.error) {
    await client.storage.from(MEDIA_BUCKET).remove([path]);
    throw scan.error;
  }
  try {
    return await invokeEdge<{ ok?: boolean; status?: string; items?: number; code?: string; error?: string }>('analyze-kitchen', { scanId: scan.data.id });
  } catch (error) {
    if (error instanceof Error && error.message.includes('OPENAI_NOT_CONFIGURED')) return { code: 'OPENAI_NOT_CONFIGURED', error: error.message };
    throw error;
  }
}

export async function uploadReceiptPhoto(householdId: string, file: File) {
  const client = ensureClient();
  const { data: auth } = await client.auth.getUser();
  if (!auth.user) throw new Error('Please sign in again.');
  const safe = file.name.toLowerCase().replace(/[^a-z0-9._-]+/g, '-').slice(-80) || 'receipt.jpg';
  const path = `${householdId}/receipts/${crypto.randomUUID()}-${safe}`;
  const upload = await client.storage.from(MEDIA_BUCKET).upload(path, file, { contentType: file.type || 'image/jpeg', upsert: false });
  if (upload.error) throw upload.error;
  const scan = await client.from('receipt_scans').insert({ household_id: householdId, object_path: path, created_by: auth.user.id }).select('id').single();
  if (scan.error) {
    await client.storage.from(MEDIA_BUCKET).remove([path]);
    throw scan.error;
  }
  try {
    return await invokeEdge<{ ok?: boolean; status?: string; items?: number; merchantName?: string | null; code?: string; error?: string; warning?: string }>('analyze-receipt', { scanId: scan.data.id });
  } catch (error) {
    if (error instanceof Error && error.message.includes('OPENAI_NOT_CONFIGURED')) return { code: 'OPENAI_NOT_CONFIGURED', error: error.message };
    throw error;
  }
}

export async function updateHouseholdMemberRemote(householdId: string, id: string, input: { name: string; role: 'adult' | 'child'; portionFactor: number; allergies: string[]; dislikes: string[]; dietaryRules: string[] }) {
  const client = ensureClient();
  const { error } = await client.from('people').update({
    display_name: input.name,
    person_type: input.role,
    portion_factor: input.portionFactor,
    allergies: input.allergies,
    dislikes: input.dislikes,
    dietary_rules: input.dietaryRules,
  }).eq('household_id', householdId).eq('id', id);
  if (error) throw error;
}
